import React, { FC, KeyboardEvent, ReactNode } from "react";
import classNames from "classnames";

/**
 * The page's one tab control: Videos | Stories, and the product-page tabs
 * when a product has two or more pages. The look is components/ViewToggle's
 * segmented control (--surface-2 pill, 32px segments, active = --surface fill
 * + bold + --sh-1), 44px below md, the page's touch floor. The behaviour is
 * the WAI-ARIA tabs pattern: one tab in the Tab order (roving tabindex), ←/→
 * move and select (wrapping), Home/End jump to the ends, and each tab names
 * the panel it shows. Too many tabs for the row scroll sideways, as the rail
 * does, rather than wrap.
 *
 *   <SegmentedTabs idPrefix="product-page" ariaLabel="Product pages" className="self-start"
 *     items={[{ id: "147", label: "amazon.com", panelId: "product-page-panel-147" }]}
 *     selectedId="147" onSelect={setSelected} />
 *   <div role="tabpanel" id="product-page-panel-147" aria-labelledby={tabId("product-page", "147")}>…</div>
 */

export interface SegmentedTab {
  id: string;
  label: ReactNode;
  /** The tabpanel this tab shows (aria-controls). */
  panelId?: string;
  /** The accessible name, when the label alone doesn't say it (a bare score number). */
  ariaLabel?: string;
  /** A tooltip, e.g. the full URL a truncated label cuts. */
  title?: string;
}

/** A tab's element id — the panel's aria-labelledby. */
export const tabId = (idPrefix: string, id: string) => `${idPrefix}-tab-${id}`;

interface Props {
  /** Unique on the page: tab ids are `${idPrefix}-tab-${id}`. */
  idPrefix: string;
  ariaLabel: string;
  items: SegmentedTab[];
  selectedId: string;
  onSelect: (id: string) => void;
  className?: string;
}

export const SegmentedTabs: FC<Props> = ({ idPrefix, ariaLabel, items, selectedId, onSelect, className }) => {
  // Automatic activation: arrowing onto a tab selects it. Every panel here is
  // already on the page or cheap to show.
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = -1;
    if (e.key === "ArrowRight") next = (index + 1) % items.length;
    else if (e.key === "ArrowLeft") next = (index - 1 + items.length) % items.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = items.length - 1;
    if (next < 0) return;
    e.preventDefault();
    const { id } = items[next];
    onSelect(id);
    // Every tab is always rendered, so the target exists now.
    document.getElementById(tabId(idPrefix, id))?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={classNames(
        "inline-flex items-center gap-[3px] p-[3px] rounded-[9px] bg-surface-2 max-w-full overflow-x-auto shrink-0",
        className,
      )}
      style={{ scrollbarWidth: "none" }}
    >
      {items.map((item, index) => {
        const active = item.id === selectedId;
        return (
          <button
            key={item.id}
            id={tabId(idPrefix, item.id)}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={item.panelId}
            aria-label={item.ariaLabel}
            title={item.title}
            tabIndex={active ? 0 : -1}
            onClick={() => onSelect(item.id)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={classNames(
              "inline-flex items-center gap-2 h-8 max-md:h-11 px-3 rounded-[7px] shrink-0 whitespace-nowrap transition-colors text-text-1",
              active && "bg-surface font-bold",
            )}
            style={{ fontSize: 12.5, boxShadow: active ? "var(--sh-1)" : "none" }}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
};
